<template>
    <div class="flex min-h-0 flex-1 flex-col">
        <!-- Display + keypad -->
        <div class="shrink-0 border-b border-slate-200/80 bg-white px-5 py-4">
            <div class="mx-auto w-full max-w-md">
                <div
                    class="rounded-2xl bg-gradient-to-br from-indigo-50 to-slate-50 p-4 ring-1 ring-indigo-100/80 dark:from-indigo-950/50 dark:to-slate-900/80 dark:ring-indigo-500/25"
                >
                    <label for="calculatorExpression" class="sr-only">Expression</label>
                    <input
                        ref="inputRef"
                        id="calculatorExpression"
                        type="text"
                        inputmode="text"
                        autocomplete="off"
                        spellcheck="false"
                        v-model="expression"
                        placeholder="0"
                        @input="onInput"
                        @keydown="onKeydown"
                        class="block w-full border-0 bg-transparent p-0 text-right text-3xl font-semibold tracking-tight text-slate-900 tabular-nums outline-hidden placeholder:text-slate-300 focus:ring-0 dark:text-white dark:placeholder:text-white/30"
                    />
                    <!-- Reserves its line either way, so the pad never shifts under the cursor. -->
                    <p v-if="errorMessage" class="mt-2 h-5 text-right text-xs font-medium text-red-600">
                        {{ errorMessage }}
                    </p>
                    <p v-else class="mt-2 h-5 text-right text-sm text-slate-500 tabular-nums">
                        <template v-if="preview !== null">= {{ formatNumber(preview) }}</template>
                    </p>
                </div>

                <!-- Functions -->
                <div class="mt-3 flex flex-wrap gap-1.5">
                    <button
                        v-for="key in functionKeys"
                        :key="key.label"
                        type="button"
                        :title="key.title"
                        @mousedown.prevent
                        @click="press(key)"
                        class="cursor-pointer rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-indigo-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        {{ key.label }}
                    </button>
                </div>

                <!-- Keypad -->
                <div class="mt-3 grid grid-cols-5 gap-2">
                    <button
                        v-for="key in padKeys"
                        :key="key.label"
                        type="button"
                        :title="key.title"
                        :disabled="key.action === 'answer' && !calcStore.getHistory.length"
                        @mousedown.prevent
                        @click="press(key)"
                        :class="[
                            toneClasses[key.tone],
                            'flex h-12 cursor-pointer items-center justify-center rounded-xl text-base font-semibold tabular-nums transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-40',
                        ]"
                    >
                        <span aria-hidden="true">{{ key.label }}</span>
                        <span class="sr-only">{{ key.srLabel || key.label }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Memory -->
        <div class="flex min-h-0 flex-1 flex-col">
            <div class="flex shrink-0 items-center justify-between gap-2 px-5 pt-4 pb-2">
                <h3 class="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    Memory
                    <span v-if="calcStore.getHistory.length" class="text-slate-400">({{ calcStore.getHistory.length }})</span>
                </h3>
                <button
                    v-if="calcStore.getHistory.length"
                    type="button"
                    @mousedown.prevent
                    @click="confirmClearHistory"
                    class="cursor-pointer rounded-lg px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50"
                >
                    Clear all
                </button>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
                <ul v-if="calcStore.getHistory.length" class="space-y-2">
                    <li
                        v-for="entry in calcStore.getHistory"
                        :key="entry.id"
                        class="group flex items-start gap-2 rounded-xl border border-slate-200/80 bg-white p-2 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
                    >
                        <button
                            type="button"
                            @mousedown.prevent
                            @click="useEntry(entry)"
                            :title="`Use ${formatNumber(entry.result)}`"
                            class="min-w-0 flex-1 cursor-pointer rounded-lg p-1.5 text-left transition hover:bg-slate-50"
                        >
                            <p class="text-xs break-words text-slate-500">{{ entry.expression }}</p>
                            <p
                                class="mt-0.5 text-sm font-semibold break-words text-slate-900 tabular-nums group-hover:text-indigo-700 dark:text-white"
                            >
                                = {{ formatNumber(entry.result) }}
                            </p>
                        </button>
                        <button
                            type="button"
                            @mousedown.prevent
                            @click="calcStore.removeCalculation(entry.id)"
                            class="shrink-0 cursor-pointer rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                            title="Remove from memory"
                        >
                            <XMarkIcon class="size-4" aria-hidden="true" />
                            <span class="sr-only">Remove this calculation</span>
                        </button>
                    </li>
                </ul>
                <div
                    v-else
                    class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center"
                >
                    <span class="mb-3 inline-flex size-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
                        <ClockIcon class="size-6" aria-hidden="true" />
                    </span>
                    <p class="text-sm font-medium text-slate-800">No calculations yet</p>
                    <p class="mt-1 max-w-xs text-xs leading-relaxed text-slate-500">
                        Every sum you finish is kept here. Tap one to drop its result back into the expression.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { ClockIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { evaluateExpression, useCalculatorStore } from '@/stores/calculator-store.js';

const calcStore = useCalculatorStore();

const expression = ref('');
const errorMessage = ref('');
const inputRef = ref(null);
// True while the field holds a finished result, so the next key either starts a
// fresh sum (a digit) or carries the result on (an operator).
const justEvaluated = ref(false);

const CONTINUATION_KEYS = new Set(['+', '-', '*', '/', '^', '%', ')', '×', '÷', '−']);

const toneClasses = {
    control: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-200/70',
    digit: 'bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 dark:text-white',
    operator: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 hover:bg-indigo-100',
    equals: 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-500',
};

const functionKeys = [
    { label: '√', insert: 'sqrt(', title: 'Square root' },
    { label: 'x²', action: 'square', title: 'Square the entry' },
];

const padKeys = [
    { label: 'AC', action: 'clearAll', tone: 'control', title: 'Clear everything (Esc)', srLabel: 'Clear everything' },
    { label: 'C', action: 'clearEntry', tone: 'control', title: 'Clear the last term', srLabel: 'Clear the last term' },
    { label: '⌫', action: 'backspace', tone: 'control', title: 'Backspace', srLabel: 'Backspace' },
    { label: '(', insert: '(', tone: 'control', srLabel: 'Open bracket' },
    { label: ')', insert: ')', tone: 'control', srLabel: 'Close bracket' },

    { label: '7', insert: '7', tone: 'digit' },
    { label: '8', insert: '8', tone: 'digit' },
    { label: '9', insert: '9', tone: 'digit' },
    { label: '÷', insert: '÷', tone: 'operator', srLabel: 'Divide' },
    { label: '%', insert: '%', tone: 'operator', title: 'Percent — 200+10% is 220', srLabel: 'Percent' },

    { label: '4', insert: '4', tone: 'digit' },
    { label: '5', insert: '5', tone: 'digit' },
    { label: '6', insert: '6', tone: 'digit' },
    { label: '×', insert: '×', tone: 'operator', srLabel: 'Multiply' },
    { label: '±', action: 'negate', tone: 'operator', title: 'Flip the sign', srLabel: 'Flip the sign' },

    { label: '1', insert: '1', tone: 'digit' },
    { label: '2', insert: '2', tone: 'digit' },
    { label: '3', insert: '3', tone: 'digit' },
    { label: '−', insert: '−', tone: 'operator', srLabel: 'Minus' },
    { label: 'ANS', action: 'answer', tone: 'control', title: 'Last result', srLabel: 'Last result' },

    { label: '0', insert: '0', tone: 'digit' },
    { label: '.', insert: '.', tone: 'digit', srLabel: 'Decimal point' },
    { label: '00', insert: '00', tone: 'digit' },
    { label: '+', insert: '+', tone: 'operator', srLabel: 'Plus' },
    { label: '=', action: 'evaluate', tone: 'equals', title: 'Calculate (Enter)', srLabel: 'Calculate' },
];

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 10 });

const formatNumber = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    const magnitude = Math.abs(number);
    if (magnitude !== 0 && (magnitude >= 1e15 || magnitude < 1e-9)) return number.toExponential(6);
    return numberFormatter.format(number);
};

// A live answer while typing; a bare number previews nothing but itself.
const preview = computed(() => {
    const text = expression.value.trim();
    if (!text || justEvaluated.value) return null;
    if (/^-?[\d,]*\.?\d*$/.test(text)) return null;
    try {
        return evaluateExpression(text);
    } catch (e) {
        return null;
    }
});

const focusInput = async ({ caret = null, selectAll = false } = {}) => {
    await nextTick();
    const el = inputRef.value;
    if (!el) return;
    el.focus();
    if (selectAll) {
        el.select();
        return;
    }
    if (caret !== null) el.setSelectionRange(caret, caret);
};

const insertText = (text) => {
    errorMessage.value = '';

    if (justEvaluated.value) {
        justEvaluated.value = false;
        if (!CONTINUATION_KEYS.has(text.charAt(0))) {
            expression.value = text;
            focusInput({ caret: text.length });
            return;
        }
        const caret = expression.value.length + text.length;
        expression.value = `${expression.value}${text}`;
        focusInput({ caret });
        return;
    }

    const el = inputRef.value;
    const start = el?.selectionStart ?? expression.value.length;
    const end = el?.selectionEnd ?? expression.value.length;
    expression.value = `${expression.value.slice(0, start)}${text}${expression.value.slice(end)}`;
    focusInput({ caret: start + text.length });
};

const clearAll = () => {
    expression.value = '';
    errorMessage.value = '';
    justEvaluated.value = false;
    focusInput();
};

// Drops the term being typed and leaves the sum before it intact.
const clearEntry = () => {
    expression.value = expression.value.replace(/[^-+*/^()×÷−%]*$/, '');
    errorMessage.value = '';
    justEvaluated.value = false;
    focusInput({ caret: expression.value.length });
};

const backspace = () => {
    const el = inputRef.value;
    const start = el?.selectionStart ?? expression.value.length;
    const end = el?.selectionEnd ?? expression.value.length;
    errorMessage.value = '';
    justEvaluated.value = false;

    if (start !== end) {
        expression.value = `${expression.value.slice(0, start)}${expression.value.slice(end)}`;
        focusInput({ caret: start });
        return;
    }
    if (start === 0) return;
    expression.value = `${expression.value.slice(0, start - 1)}${expression.value.slice(start)}`;
    focusInput({ caret: start - 1 });
};

// Squares the whole entry rather than just the last digit typed.
const square = () => {
    const text = expression.value.trim();
    if (!text) return;
    const body = /^[\d.]+$/.test(text) ? text : `(${text})`;
    expression.value = `${body}^2`;
    errorMessage.value = '';
    justEvaluated.value = false;
    focusInput({ caret: expression.value.length });
};

const negate = () => {
    const text = expression.value.trim();
    if (!text) {
        insertText('-');
        return;
    }
    // Only unwrap a plain -( … ) so nothing that nests brackets gets mangled.
    const wrapped = /^-\(([^()]*)\)$/.exec(text);
    expression.value = wrapped ? wrapped[1] : `-(${text})`;
    errorMessage.value = '';
    justEvaluated.value = false;
    focusInput({ caret: expression.value.length });
};

const evaluate = () => {
    if (justEvaluated.value) return;

    const text = expression.value.trim();
    if (!text) return;

    let value;
    try {
        value = evaluateExpression(text);
    } catch (error) {
        errorMessage.value = error.message;
        focusInput();
        return;
    }

    errorMessage.value = '';
    calcStore.addCalculation(text, value);
    expression.value = String(value);
    justEvaluated.value = true;
    focusInput({ selectAll: true });
};

const useAnswer = () => {
    const latest = calcStore.getHistory[0];
    if (!latest) return;
    insertText(String(latest.result));
};

const useEntry = (entry) => {
    insertText(String(entry.result));
};

const confirmClearHistory = () => {
    if (!calcStore.getHistory.length) return;
    if (window.confirm('Clear every saved calculation? This cannot be undone.')) {
        calcStore.clearHistory();
    }
    focusInput();
};

const press = (key) => {
    if (key.insert) {
        insertText(key.insert);
        return;
    }
    if (key.action === 'clearAll') return clearAll();
    if (key.action === 'clearEntry') return clearEntry();
    if (key.action === 'backspace') return backspace();
    if (key.action === 'square') return square();
    if (key.action === 'negate') return negate();
    if (key.action === 'answer') return useAnswer();
    if (key.action === 'evaluate') return evaluate();
};

const onInput = () => {
    errorMessage.value = '';
    justEvaluated.value = false;
};

const onKeydown = (event) => {
    if (event.key === 'Enter' || event.key === '=') {
        event.preventDefault();
        evaluate();
        return;
    }

    if (event.key === 'Escape') {
        // Clears first; a second Escape on an empty pad closes the panel.
        if (expression.value !== '' || errorMessage.value !== '') {
            event.preventDefault();
            event.stopPropagation();
            clearAll();
        }
        return;
    }

    if (!justEvaluated.value) return;
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

    const el = inputRef.value;
    if (!el) return;
    if (CONTINUATION_KEYS.has(event.key)) {
        const caret = expression.value.length;
        el.setSelectionRange(caret, caret);
    } else {
        el.select();
    }
    justEvaluated.value = false;
};

// Switching back from the interior tab keeps the panel open, so there is no
// transition to wait for; the parent calls focus() again once a fresh panel has
// finished sliding in and the dialog has stopped claiming focus for itself.
onMounted(() => focusInput());

defineExpose({ focus: () => focusInput() });
</script>
